/* ============================================================
   ProductReviewsBlock (054819ad) — published product reviews on
   the public product page. Display-only v1: average + list from
   GET /api/v1/reviews/products/{productId} (no submission form —
   that is a later card). Light theme, iOS-18 minimal.
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ProductReviewsPage } from '../types/product';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ru-RU');
  } catch {
    return iso.slice(0, 10);
  }
}

function num(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function ProductReviewsBlock({ productId }: { productId: string }) {
  const [page, setPage] = useState<ProductReviewsPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);
    api.getProductReviews(productId)
      .then((p) => { if (!cancelled) setPage(p); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Ошибка загрузки отзывов'); });
    return () => { cancelled = true; };
  }, [productId]);

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginBottom: 'var(--space-sm)' }}>
          Отзывы о товаре
        </h3>
        <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
      </div>
    );
  }

  if (!page) {
    return <div className="skeleton" style={{ height: 120, borderRadius: 'var(--radius-lg)' }} />;
  }

  return (
    <div className="card" style={{ padding: 'var(--space-lg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Отзывы о товаре</h3>
        <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
          {page.averageRating === null
            ? 'Нет отзывов'
            : `★ ${num(page.averageRating).toFixed(1)} · ${num(page.reviewCount)} отзыв(ов)`}
        </span>
      </div>
      {page.items.length > 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-md)', marginTop: 'var(--space-md)' }}>
          {page.items.map((r) => (
            <div key={r.id} style={{ borderTop: '1px solid var(--color-separator)', paddingTop: 'var(--space-md)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{r.authorName}</span>
                <span style={{ color: 'var(--color-primary)' }}>
                  {'★'.repeat(Math.max(0, Math.min(5, num(r.rating))))}
                </span>
              </div>
              <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)', whiteSpace: 'pre-wrap' }}>
                {r.text}
              </p>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)' }}>
                {formatDate(r.createdAtUtc)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-md)' }}>
          Покупатели ещё не оставили отзывы об этом товаре
        </p>
      )}
    </div>
  );
}
